import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgbModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertService } from '@app/_services';
import { ConfigDataService } from '@app/_services/config-data.service';
import { first } from 'rxjs/operators';

export interface ModelData {
  make: string;
  productType: string;
  model: string[];
}

@Component({
  selector: 'app-model-manager',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    NgbModule
  ],
  templateUrl: './model-manager.component.html',
  styleUrls: ['./model-manager.component.scss']
})
export class ModelManagerComponent implements OnInit {
  modelForm: FormGroup;
  editForm: FormGroup;
  modelData: ModelData[] = [];
  filteredData: ModelData[] = [];
  loading = false;
  submitting = false;
  searchTerm = '';
  selectedProductType = '';
  editingIndex = -1;
  selectedMake: ModelData | null = null;
  newModelName = '';
  
  productTypes = ['Two Wheeler', 'Three Wheeler', 'Four Wheeler'];
  
  // Summary statistics
  get totalMakes(): number {
    return this.modelData.length;
  }
  
  get totalModels(): number {
    return this.modelData.reduce((total, make) => total + make.model.length, 0);
  }
  
  get twoWheelerCount(): number {
    return this.modelData.filter(m => m.productType === 'Two Wheeler').length;
  }
  
  get threeWheelerCount(): number {
    return this.modelData.filter(m => m.productType === 'Three Wheeler').length;
  }
  
  get fourWheelerCount(): number {
    return this.modelData.filter(m => m.productType === 'Four Wheeler').length;
  }

  constructor(
    private formBuilder: FormBuilder,
    private configDataService: ConfigDataService,
    private alertService: AlertService,
    private modalService: NgbModal
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.loadModelData();
  }

  private initializeForms(): void {
    this.modelForm = this.formBuilder.group({
      make: ['', [Validators.required, Validators.minLength(2)]],
      productType: ['', Validators.required],
      models: this.formBuilder.array([this.createModelControl()])
    });

    this.editForm = this.formBuilder.group({
      make: ['', [Validators.required, Validators.minLength(2)]],
      productType: ['', Validators.required]
    });
  }

  private createModelControl(): FormGroup {
    return this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(1)]]
    });
  }

  get modelsArray(): FormArray {
    return this.modelForm.get('models') as FormArray;
  }

  addModelField(): void {
    this.modelsArray.push(this.createModelControl());
  }

  removeModelField(index: number): void {
    if (this.modelsArray.length > 1) {
      this.modelsArray.removeAt(index);
    }
  }

  private loadModelData(): void {
    this.loading = true;
    this.configDataService.getConfig('Model')
      .pipe(first())
      .subscribe({
        next: (data: ModelData[]) => {
          this.modelData = data || [];
          this.applyFilters();
          this.loading = false;
        },
        error: (error) => {
          this.alertService.error('Failed to load model data: ' + error);
          this.loading = false;
        }
      });
  }

  onSubmit(): void {
    if (this.modelForm.valid) {
      this.submitting = true;
      
      const formValue = this.modelForm.value;
      const newMake: ModelData = {
        make: formValue.make.trim(),
        productType: formValue.productType,
        model: formValue.models
          .map((m: any) => m.name.trim())
          .filter((name: string) => name.length > 0)
      };

      // Check if make already exists
      const existingIndex = this.modelData.findIndex(
        m => m.make.toLowerCase() === newMake.make.toLowerCase() && 
             m.productType === newMake.productType
      );

      if (existingIndex >= 0) {
        this.alertService.error('A make with this name and product type already exists.');
        this.submitting = false;
        return;
      }

      this.modelData.push(newMake);
      this.saveModelData();
    } else {
      this.markFormGroupTouched(this.modelForm);
    }
  }

  editMake(make: ModelData, modal: any): void {
    this.selectedMake = { ...make };
    this.editForm.patchValue({
      make: make.make,
      productType: make.productType
    });
    this.modalService.open(modal, { size: 'lg' });
  }

  updateMake(): void {
    if (this.editForm.valid && this.selectedMake) {
      const index = this.modelData.findIndex(
        m => m.make === this.selectedMake!.make && 
             m.productType === this.selectedMake!.productType
      );
      
      if (index >= 0) {
        this.modelData[index] = {
          ...this.modelData[index],
          make: this.editForm.value.make.trim(),
          productType: this.editForm.value.productType
        };
        
        this.saveModelData();
        this.modalService.dismissAll();
      }
    } else {
      this.markFormGroupTouched(this.editForm);
    }
  }

  deleteMake(make: ModelData): void {
    if (confirm(`Are you sure you want to delete "${make.make}" and all its models?`)) {
      const index = this.modelData.findIndex(
        m => m.make === make.make && m.productType === make.productType
      );
      
      if (index >= 0) {
        this.modelData.splice(index, 1);
        this.saveModelData();
      }
    }
  }

  addModel(make: ModelData): void {
    if (this.newModelName.trim()) {
      const index = this.modelData.findIndex(
        m => m.make === make.make && m.productType === make.productType
      );
      
      if (index >= 0) {
        if (!this.modelData[index].model.includes(this.newModelName.trim())) {
          this.modelData[index].model.push(this.newModelName.trim());
          this.modelData[index].model.sort();
          this.saveModelData();
          this.newModelName = '';
        } else {
          this.alertService.error('Model already exists for this make.');
        }
      }
    }
  }

  deleteModel(make: ModelData, modelName: string): void {
    if (confirm(`Are you sure you want to delete model "${modelName}"?`)) {
      const makeIndex = this.modelData.findIndex(
        m => m.make === make.make && m.productType === make.productType
      );
      
      if (makeIndex >= 0) {
        const modelIndex = this.modelData[makeIndex].model.indexOf(modelName);
        if (modelIndex >= 0) {
          this.modelData[makeIndex].model.splice(modelIndex, 1);
          this.saveModelData();
        }
      }
    }
  }

  private saveModelData(): void {
    const payload = {
      keyValue: 'Model',
      configType: 'Array',
      ResultValue: JSON.stringify(this.modelData)
    };

    this.configDataService.resetStringCacheConfig(payload)
      .pipe(first())
      .subscribe({
        next: () => {
          this.alertService.success('Model data updated successfully');
          this.applyFilters();
          this.resetForm();
          this.submitting = false;
        },
        error: (error) => {
          this.alertService.error('Failed to save model data: ' + error);
          this.submitting = false;
        }
      });
  }

  applyFilters(): void {
    let filtered = [...this.modelData];

    if (this.searchTerm) {
      const searchLower = this.searchTerm.toLowerCase();
      filtered = filtered.filter(make => 
        make.make.toLowerCase().includes(searchLower) ||
        make.model.some(model => model.toLowerCase().includes(searchLower))
      );
    }

    if (this.selectedProductType) {
      filtered = filtered.filter(make => make.productType === this.selectedProductType);
    }

    this.filteredData = filtered.sort((a, b) => a.make.localeCompare(b.make));
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedProductType = '';
    this.applyFilters();
  }

  exportData(): void {
    const dataStr = JSON.stringify(this.modelData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `model-data-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private resetForm(): void {
    this.modelForm.reset();
    this.modelsArray.clear();
    this.modelsArray.push(this.createModelControl());
    this.newModelName = '';
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(arrayControl => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl);
          } else {
            arrayControl.markAsTouched();
          }
        });
      }
    });
  }

  // Helper methods for template
  isFieldInvalid(fieldName: string, form: FormGroup = this.modelForm): boolean {
    const field = form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string, form: FormGroup = this.modelForm): string {
    const field = form.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) return `${fieldName} is required`;
      if (field.errors['minlength']) return `${fieldName} must be at least ${field.errors['minlength'].requiredLength} characters`;
    }
    return '';
  }

  trackByMake(index: number, make: ModelData): string {
    return `${make.make}-${make.productType}`;
  }
}
